import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';

/**
 * AES key/IV for SMS create_order and payments/verify responses only.
 * Must match backend encryption for those endpoints.
 */
const PAYMENT_KEY = '89H49I12T20E542N17D4E5A47R184LKL';
const PAYMENT_IV = '0543737198408118';

function isIvWrappedCipherObject(r: Record<string, unknown>): boolean {
  const ivRaw = r['iv'] ?? r['IV'] ?? r['nonce'];
  if (typeof ivRaw !== 'string') return false;
  const ct =
    (typeof r['value'] === 'string' ? r['value'] : null) ??
    (typeof r['ciphertext'] === 'string' ? r['ciphertext'] : null) ??
    (typeof r['encrypted'] === 'string' ? r['encrypted'] : null) ??
    (typeof r['payload'] === 'string' ? r['payload'] : null) ??
    (typeof r['data'] === 'string' ? r['data'] : null) ??
    (typeof r['result'] === 'string' ? r['result'] : null);
  return typeof ct === 'string';
}

@Injectable({
  providedIn: 'root'
})
export class PaymentDecryptService {
  private readonly keyWa = CryptoJS.enc.Utf8.parse(PAYMENT_KEY);
  private readonly ivWa = CryptoJS.enc.Utf8.parse(PAYMENT_IV);

  /**
   * After HTTP response: if body matches encrypted envelope, decrypt with payment key/IV; else return as-is.
   */
  decryptResponseBody(body: unknown): unknown {
    if (!this.isEncryptedResponse(body)) {
      return body;
    }
    const enc = PaymentDecryptService.extractEncryptedString(body);
    if (enc == null) {
      return body;
    }
    try {
      const plain = this.decryptPayloadString(enc);
      const parsed = this.parseDecryptedPayload(plain);
      // console.log('[PaymentDecrypt] decrypted payload:', parsed);
      return parsed;
    } catch (e) {
      console.warn('[PaymentDecrypt] decrypt failed, using raw body', e);
      return body;
    }
  }

  /** Decrypt error payloads (e.g. HttpErrorResponse.error) when the API uses the same scheme. */
  decryptErrorPayload(errorBody: unknown): unknown {
    return this.decryptResponseBody(errorBody);
  }

  isEncryptedResponse(response: unknown): boolean {
    if (typeof response === 'string') {
      return this.looksLikeEncryptedString(response);
    }
    if (response && typeof response === 'object') {
      const r = response as Record<string, unknown>;
      if (typeof r['encrypted_data'] === 'string') {
        return this.looksLikeEncryptedString(r['encrypted_data']);
      }
      /** SMS create_order / verify: `{ code: 1, result: "<hex ciphertext>" }` */
      if (typeof r['result'] === 'string') {
        return this.looksLikeEncryptedString(r['result']);
      }
      if (typeof r['data'] === 'string') {
        return this.looksLikeEncryptedString(r['data']);
      }
      if (isIvWrappedCipherObject(r)) {
        return true;
      }
      const nested = r['data'];
      if (nested && typeof nested === 'object') {
        const n = nested as Record<string, unknown>;
        if (typeof n['encrypted_data'] === 'string') {
          return this.looksLikeEncryptedString(n['encrypted_data']);
        }
        if (typeof n['result'] === 'string') {
          return this.looksLikeEncryptedString(n['result']);
        }
        if (typeof n['data'] === 'string') {
          return this.looksLikeEncryptedString(n['data']);
        }
      }
    }
    return false;
  }

  static extractEncryptedString(body: unknown): string | null {
    if (body == null) return null;
    if (typeof body === 'string') return body;
    if (typeof body !== 'object') return null;
    const r = body as Record<string, unknown>;
    if (isIvWrappedCipherObject(r)) {
      return JSON.stringify(body);
    }
    if (typeof r['encrypted_data'] === 'string') return r['encrypted_data'];
    if (typeof r['result'] === 'string') return r['result'];
    if (typeof r['data'] === 'string') return r['data'];
    const nested = r['data'];
    if (nested && typeof nested === 'object') {
      const n = nested as Record<string, unknown>;
      if (typeof n['encrypted_data'] === 'string') return n['encrypted_data'];
      if (typeof n['result'] === 'string') return n['result'];
      if (typeof n['data'] === 'string') return n['data'];
    }
    return null;
  }

  private looksLikeEncryptedString(data: string): boolean {
    const t = data.trim();
    /** Hex ciphertext (e.g. API `result` field) */
    if (t.length >= 32 && /^[0-9a-fA-F]+$/.test(t) && t.length % 2 === 0) {
      return true;
    }
    if (t.length >= 20 && /^[A-Za-z0-9+/=_-]+$/.test(t)) {
      return true;
    }
    if (t.startsWith('{')) {
      try {
        const o = JSON.parse(t) as Record<string, unknown>;
        return isIvWrappedCipherObject(o);
      } catch {
        return false;
      }
    }
    return false;
  }

  /**
   * Decrypt: base64 or hex ciphertext with fixed UTF-8 key + IV (AES-CBC, PKCS7).
   * If `encryptedData` is a JSON string with iv+ciphertext fields, decrypt the ciphertext part with fixed IV/key (same as backend contract).
   */
  private decryptPayloadString(encryptedData: string): string {
    const s = encryptedData.trim();
    if (s.startsWith('{')) {
      try {
        const o = JSON.parse(s) as Record<string, unknown>;
        const ctRaw =
          (typeof o['value'] === 'string' ? o['value'] : null) ??
          (typeof o['ciphertext'] === 'string' ? o['ciphertext'] : null) ??
          (typeof o['encrypted'] === 'string' ? o['encrypted'] : null) ??
          (typeof o['payload'] === 'string' ? o['payload'] : null) ??
          (typeof o['result'] === 'string' ? o['result'] : null) ??
          (typeof o['data'] === 'string' ? o['data'] : null);
        if (typeof ctRaw === 'string') {
          return this.decryptCipherTextString(ctRaw);
        }
      } catch {
        /* fall through */
      }
    }
    return this.decryptCipherTextString(s);
  }

  private decryptCipherTextString(ct: string): string {
    const t = ct.trim();
    let ctWa: CryptoJS.lib.WordArray;
    if (/^[0-9a-fA-F]+$/.test(t) && t.length % 2 === 0) {
      ctWa = CryptoJS.enc.Hex.parse(t.replace(/^0x/i, ''));
    } else {
      ctWa = CryptoJS.enc.Base64.parse(t);
    }
    const dec = CryptoJS.AES.decrypt({ ciphertext: ctWa } as CryptoJS.lib.CipherParams, this.keyWa, {
      iv: this.ivWa,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    const out = dec.toString(CryptoJS.enc.Utf8);
    if (!out) {
      throw new Error('Payment AES decrypt produced empty plaintext');
    }
    return out;
  }

  private parseDecryptedPayload(text: string): unknown {
    let t = text.replace(/\0+$/g, '').trim();
    if (
      (t.startsWith('{') && t.endsWith('}')) ||
      (t.startsWith('[') && t.endsWith(']')) ||
      t.startsWith('"')
    ) {
      try {
        return JSON.parse(t);
      } catch {
        /* keep string */
      }
    }
    return t;
  }
}
